export default defineNuxtPlugin(() => {
  const config = useRuntimeConfig()
  
  // Google Analytics 4 Integration for SEO tracking
  const GA_MEASUREMENT_ID = config.public.googleAnalyticsId || process.env.GOOGLE_ANALYTICS_ID
  
  if (process.client && GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    // Load Google Analytics
    const script = document.createElement('script')
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
    document.head.appendChild(script)
    
    // Initialize gtag
    window.dataLayer = window.dataLayer || []
    function gtag(...args: any[]) {
      window.dataLayer.push(args)
    }
    
    // Make gtag globally available
    window.gtag = gtag
    
    gtag('js', new Date())
    gtag('config', GA_MEASUREMENT_ID, {
      // Enhanced e-commerce and SEO tracking
      enhanced_conversions: true,
      link_attribution: true,
      page_title: document.title,
      page_location: window.location.href,
      // Track Core Web Vitals
      custom_map: {
        'custom_parameter_1': 'web_vitals_metric'
      }
    })
    
    // Track SEO-important events
    const router = useRouter()
    router.afterEach((to) => {
      // Skip tracking for AMP pages (they have their own analytics)
      if (to.fullPath.includes('amp=1')) {
        return
      }
      
      gtag('config', GA_MEASUREMENT_ID, {
        page_path: to.fullPath,
        page_title: document.title,
        page_location: window.location.href
      })
      
      // Track internal navigation for SEO
      gtag('event', 'page_view', {
        page_path: to.fullPath,
        page_title: document.title,
        event_category: 'Navigation',
        custom_parameter_1: 'internal_link'
      })
    })
    
    // Core Web Vitals tracking for SEO
    const observeWebVitals = () => {
      // Skip Web Vitals tracking for AMP pages (they have their own analytics)
      if (typeof window !== 'undefined' && window.location.search.includes('amp=1')) {
        return
      }

      // Largest Contentful Paint
      new PerformanceObserver((entryList) => {
        for (const entry of entryList.getEntries()) {
          gtag('event', 'web_vitals', {
            name: 'LCP',
            value: Math.round(entry.renderTime || entry.loadTime),
            event_category: 'Web Vitals'
          })
        }
      }).observe({ entryTypes: ['largest-contentful-paint'] })

      // Cumulative Layout Shift
      new PerformanceObserver((entryList) => {
        let clsValue = 0
        for (const entry of entryList.getEntries()) {
          if (!entry.hadRecentInput) {
            clsValue += entry.value
          }
        }
        if (clsValue > 0) {
          gtag('event', 'web_vitals', {
            name: 'CLS',
            value: Math.round(clsValue * 1000),
            event_category: 'Web Vitals'
          })
        }
      }).observe({ entryTypes: ['layout-shift'] })
    }
    
    // Initialize Web Vitals tracking
    observeWebVitals()
    
    // Track search queries if implemented
    const trackSiteSearch = (searchTerm: string, category?: string) => {
      gtag('event', 'search', {
        search_term: searchTerm,
        content_category: category || 'general',
        event_category: 'Site Search'
      })
    }
    
    // Track form submissions (lead generation)
    const trackFormSubmission = (formName: string, formLocation: string) => {
      gtag('event', 'generate_lead', {
        event_category: 'Form',
        event_label: formName,
        form_location: formLocation,
        value: 1
      })
    }
    
    // Track CTA clicks
    const trackCTAClick = (ctaText: string, ctaLocation: string) => {
      gtag('event', 'select_promotion', {
        event_category: 'CTA',
        event_label: ctaText,
        promotion_name: ctaText,
        creative_slot: ctaLocation
      })
    }
    
    // Make tracking functions globally available
    window.trackSiteSearch = trackSiteSearch
    window.trackFormSubmission = trackFormSubmission
    window.trackCTAClick = trackCTAClick
  }
  
  // Google Search Console verification
  const searchConsoleCode = config.public.googleSearchConsoleCode || process.env.GOOGLE_SEARCH_CONSOLE_CODE
  if (searchConsoleCode) {
    useHead({
      meta: [
        { name: 'google-site-verification', content: searchConsoleCode }
      ]
    })
  }
  
  // Bing Webmaster Tools verification
  const bingCode = config.public.bingVerificationCode || process.env.BING_VERIFICATION_CODE
  if (bingCode) {
    useHead({
      meta: [
        { name: 'msvalidate.01', content: bingCode }
      ]
    })
  }
})